import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import InstitutionCampusDropdown from '../components/InstitutionCampusDropdown';
import axios from '../api/Axios';

const HomeFormJSON = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    institution: '',
    campus: '',
    year: '',
    checkIn: '',
    checkOut: '',
    gender: '',
    roomType: '',
    bathroomType: ''
  });
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [institutionTerms, setInstitutionTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState('');
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If room type is changed to Studio, auto-set bathroom to Private
    if (name === 'roomType' && value === 'Studio') {
      setFormData({
        ...formData,
        [name]: value,
        bathroomType: 'Private'
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Fetch terms when institution and year are selected
  useEffect(() => {
    const fetchInstitutionTerms = async () => {
      if (formData.institution && formData.year) {
        try {
          const response = await axios.get(`api/terms/view/?institution=${formData.institution}&academic_year=${formData.year}`);
          setInstitutionTerms(response.data || []);
        } catch (err) {
          console.error('Failed to fetch institution terms:', err);
          setInstitutionTerms([]);
        }
      } else {
        setInstitutionTerms([]);
      }
    };
    fetchInstitutionTerms();
  }, [formData.institution, formData.year]);

  // Auto-select first available term when terms are loaded
  useEffect(() => {
    if (institutionTerms.length > 0) {
      const term = institutionTerms[0];
      // Find the first available term type
      if (term.semester_one_start && term.semester_one_end) {
        setSelectedTerm('semester_one');
      } else if (term.semester_two_start && term.semester_two_end) {
        setSelectedTerm('semester_two');
      } else if (term.trimester_one_start && term.trimester_one_end) {
        setSelectedTerm('trimester_one');
      } else if (term.trimester_two_start && term.trimester_two_end) {
        setSelectedTerm('trimester_two');
      } else if (term.trimester_three_start && term.trimester_three_end) {
        setSelectedTerm('trimester_three');
      } else if (term.annual_start && term.annual_end) {
        setSelectedTerm('annual');
      }
    } else {
      setSelectedTerm('');
    }
  }, [institutionTerms]);

  // Auto-populate check-in/check-out dates when term is selected
  useEffect(() => {
    if (selectedTerm && institutionTerms.length > 0) {
      const dates = getSelectedTermDates();
      if (dates.startDate && dates.endDate) {
        setFormData(prev => ({
          ...prev,
          checkIn: dates.startDate,
          checkOut: dates.endDate
        }));
      }
    }
  }, [selectedTerm]);

  // Get the date range for the selected term or year
  const getSelectedTermDates = () => {
    // If a term is selected and we have term data, use term dates
    if (selectedTerm && institutionTerms.length > 0) {
      const term = institutionTerms[0];
      const termMap = {
        'semester_one': { start: term.semester_one_start, end: term.semester_one_end },
        'semester_two': { start: term.semester_two_start, end: term.semester_two_end },
        'trimester_one': { start: term.trimester_one_start, end: term.trimester_one_end },
        'trimester_two': { start: term.trimester_two_start, end: term.trimester_two_end },
        'trimester_three': { start: term.trimester_three_start, end: term.trimester_three_end },
        'annual': { start: term.annual_start, end: term.annual_end }
      };
      
      const dates = termMap[selectedTerm] || { start: '', end: '' };
      return { startDate: dates.start || '', endDate: dates.end || '' };
    }
    
    // If no term selected but year is selected, lock to year boundaries
    if (formData.year) {
      return { 
        startDate: `${formData.year}-01-01`, 
        endDate: `${formData.year}-12-31` 
      };
    }
    
    // No year selected, no constraints
    return { startDate: '', endDate: '' };
  };

  // For InstitutionCampusDropdown
  const handleInstitutionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      institution: value,
      campus: '', // reset campus when institution changes
      year: '', // reset year when institution changes
      checkIn: '', // reset dates
      checkOut: '' // reset dates
    }));
    setSelectedTerm(''); // reset selected term
  };
  const handleCampusChange = (value) => {
    setFormData(prev => ({
      ...prev,
      campus: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');

    // Validate form data
    if (!formData.campus) {
      setErrorMsg('Please select a campus to search for accommodation.');
      return;
    }

    setLoading(true);

    try {
      // Prepare search parameters
      const searchParams = {
        institution: formData.institution,
        campus: formData.campus,
        checkIn: formData.checkIn,
        checkOut: formData.checkOut,
        gender: formData.gender,
        roomType: formData.roomType,
        bathroomType: formData.bathroomType
      };

      // Navigate to JSON search results page with parameters
      navigate('/json-property-search-results', { 
        state: { searchParams } 
      });
    } catch (err) {
      setErrorMsg("Search failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div style={{
      backgroundColor: 'rgba(255, 255, 255, 0.17)',
      color: 'rgba(255, 255, 255)',
      padding: '2rem',
      margin: '1rem 0',
      boxShadow: '-2px 1px 5px 0px rgba(0,0,0,0.10)',
      WebkitBoxShadow: '-2px 1px 5px 0px rgba(0,0,0,0.10)',
      MozBoxShadow: '-2px 1px 5px 0px rgba(0,0,0,0.10)'
    }}>
      <style jsx="true">{`
        .form-label {
          color: white !important;
          font-weight: 500;
        }
        
        .form-floating label {
          color: white !important;
        }
        
        .form-floating > .form-select:focus ~ label,
        .form-floating > .form-select:not(:placeholder-shown) ~ label {
          color: white !important;
        }
      `}</style>
      <h3 style={{ marginBottom: '1.5rem', color: 'white' }}>JSON Property Search</h3>
      <form onSubmit={handleSubmit}>
        <InstitutionCampusDropdown
          institutionValue={formData.institution}
          campusValue={formData.campus}
          onInstitutionChange={handleInstitutionChange}
          onCampusChange={handleCampusChange}
        />

        {/* Year Selection */}
        {formData.institution && (
          <div className="mb-3">
            <label className="form-label">Academic Year</label>
            <select
              className="form-control"
              name="year"
              value={formData.year}
              onChange={handleChange}
            >
              <option value="">Select Academic Year</option>
              <option value={currentYear}>{currentYear}</option>
              <option value={nextYear}>{nextYear}</option>
            </select>
          </div>
        )}

        {/* Display Institution Terms */}
        {formData.institution && formData.year && institutionTerms.length > 0 && (
          <div className="mb-3" style={{ 
            backgroundColor: 'white', 
            padding: '1rem', 
            borderRadius: '0.375rem',
            border: '1px solid rgba(0, 0, 0, 0.2)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h6 style={{ color: 'black', marginBottom: '1rem', fontWeight: 'bold' }}>Academic Terms</h6>
            {institutionTerms.map((term, index) => (
              <div key={index} style={{ marginBottom: '1rem' }}>
                {term.academic_year && (
                  <div style={{ 
                    color: 'black', 
                    fontWeight: 'bold', 
                    marginBottom: '0.5rem',
                    fontSize: '1.1rem',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.2)',
                    paddingBottom: '0.25rem'
                  }}>
                    Academic Year: {term.academic_year}
                  </div>
                )}
                <div className="row" style={{ fontSize: '0.9rem' }}>
                  {/* Semester One */}
                  {term.semester_one_start && term.semester_one_end && (
                    <div className="col-md-6 mb-2">
                      <div style={{ color: 'black', display: 'flex', alignItems: 'center' }}>
                        <input
                          type="radio"
                          name="selectedTerm"
                          value="semester_one"
                          checked={selectedTerm === 'semester_one'}
                          onChange={(e) => setSelectedTerm(e.target.value)}
                          style={{ marginRight: '0.5rem' }}
                        />
                        <div>
                          <strong>Semester 1:</strong><br />
                          {new Date(term.semester_one_start).toLocaleDateString()} - {new Date(term.semester_one_end).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Semester Two */}
                  {term.semester_two_start && term.semester_two_end && (
                    <div className="col-md-6 mb-2">
                      <div style={{ color: 'black', display: 'flex', alignItems: 'center' }}>
                        <input
                          type="radio"
                          name="selectedTerm"
                          value="semester_two"
                          checked={selectedTerm === 'semester_two'}
                          onChange={(e) => setSelectedTerm(e.target.value)}
                          style={{ marginRight: '0.5rem' }}
                        />
                        <div>
                          <strong>Semester 2:</strong><br />
                          {new Date(term.semester_two_start).toLocaleDateString()} - {new Date(term.semester_two_end).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Trimester One */}
                  {term.trimester_one_start && term.trimester_one_end && (
                    <div className="col-md-6 mb-2">
                      <div style={{ color: 'black', display: 'flex', alignItems: 'center' }}>
                        <input
                          type="radio"
                          name="selectedTerm"
                          value="trimester_one"
                          checked={selectedTerm === 'trimester_one'}
                          onChange={(e) => setSelectedTerm(e.target.value)}
                          style={{ marginRight: '0.5rem' }}
                        />
                        <div>
                          <strong>Trimester 1:</strong><br />
                          {new Date(term.trimester_one_start).toLocaleDateString()} - {new Date(term.trimester_one_end).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Trimester Two */}
                  {term.trimester_two_start && term.trimester_two_end && (
                    <div className="col-md-6 mb-2">
                      <div style={{ color: 'black', display: 'flex', alignItems: 'center' }}>
                        <input
                          type="radio"
                          name="selectedTerm"
                          value="trimester_two"
                          checked={selectedTerm === 'trimester_two'}
                          onChange={(e) => setSelectedTerm(e.target.value)}
                          style={{ marginRight: '0.5rem' }}
                        />
                        <div>
                          <strong>Trimester 2:</strong><br />
                          {new Date(term.trimester_two_start).toLocaleDateString()} - {new Date(term.trimester_two_end).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Trimester Three */}
                  {term.trimester_three_start && term.trimester_three_end && (
                    <div className="col-md-6 mb-2">
                      <div style={{ color: 'black', display: 'flex', alignItems: 'center' }}>
                        <input
                          type="radio"
                          name="selectedTerm"
                          value="trimester_three"
                          checked={selectedTerm === 'trimester_three'}
                          onChange={(e) => setSelectedTerm(e.target.value)}
                          style={{ marginRight: '0.5rem' }}
                        />
                        <div>
                          <strong>Trimester 3:</strong><br />
                          {new Date(term.trimester_three_start).toLocaleDateString()} - {new Date(term.trimester_three_end).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Annual Period */}
                  {term.annual_start && term.annual_end && (
                    <div className="col-md-6 mb-2">
                      <div style={{ color: 'black', display: 'flex', alignItems: 'center' }}>
                        <input
                          type="radio"
                          name="selectedTerm"
                          value="annual"
                          checked={selectedTerm === 'annual'}
                          onChange={(e) => setSelectedTerm(e.target.value)}
                          style={{ marginRight: '0.5rem' }}
                        />
                        <div>
                          <strong>Annual Period:</strong><br />
                          {new Date(term.annual_start).toLocaleDateString()} - {new Date(term.annual_end).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Check In</label>
            <input
              className="form-control"
              type="date"
              name="checkIn"
              value={formData.checkIn}
              onChange={handleChange}
              min={getSelectedTermDates().startDate}
              max={getSelectedTermDates().endDate}
              disabled={!formData.year}
              required
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Check Out</label>
            <input
              className="form-control"
              type="date"
              name="checkOut"
              value={formData.checkOut}
              onChange={handleChange}
              min={getSelectedTermDates().startDate}
              max={getSelectedTermDates().endDate}
              disabled={!formData.year}
              required
            />
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label">Gender</label>
          <select
            className="form-control"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Room Type</label>
            <select
              className="form-control"
              name="roomType"
              value={formData.roomType}
              onChange={handleChange}
            >
              <option value="">Select Type</option>
              <option value="Private">Private</option>
              <option value="Shared">Shared</option>
              <option value="Studio">Studio</option>
            </select>
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Bathroom</label>
            <select
              className="form-control"
              name="bathroomType"
              value={formData.bathroomType}
              onChange={handleChange}
              disabled={formData.roomType === 'Studio'}
            >
              <option value="">Select Bathroom</option>
              <option value="Private">Private</option>
              <option value="Shared">Shared</option>
            </select>
          </div>
        </div>
        {errorMsg && (
          <div className="alert alert-danger mt-2">
            {errorMsg}
          </div>
        )}
        {successMsg && (
          <div className="alert alert-success mt-2">{successMsg}</div>
        )}
        <button
          className="btn btn-primary w-100"
          type="submit"
          disabled={loading}
          style={{
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            padding: '10px 30px'
          }}
        >
          {loading ? "Searching..." : "Search Properties (JSON)"}
        </button>
      </form>
    </div>
  );
};

export default HomeFormJSON;
